import React, { useState, useCallback } from 'react';
import {
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  CheckIcon,
  XMarkIcon,
  VideoCameraIcon,
  MapPinIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { BookingSettings } from '../../types/appointments';
import { appointmentService } from '../../services/appointmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../common/Button';
import GoogleAuthButton from './GoogleAuthButton';
import { Switch } from '../common/Switch';

interface BookingSettingsFormProps {
  settings: BookingSettings | null;
  onSave: (settings: BookingSettings) => void;
  onCancel: () => void;
}

const BookingSettingsForm: React.FC<BookingSettingsFormProps> = ({
  settings,
  onSave,
  onCancel
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'basic' | 'schedule' | 'collection' | 'meeting' | 'notifications'>('basic');
  const [formData, setFormData] = useState<Partial<BookingSettings>>({
    title: settings?.title || '',
    description: settings?.description || '',
    duration: settings?.duration || 60,
    availableHours: settings?.availableHours || {
      start: '09:00',
      end: '17:00',
      days: [1, 2, 3, 4, 5] // Hétfő-Péntek
    },
    bufferTime: settings?.bufferTime || 15,
    advanceBooking: settings?.advanceBooking || {
      min: 2, // 2 óra
      max: 30 // 30 nap
    },
    requireConfirmation: settings?.requireConfirmation || false,
    crmOptions: { logOnCustomer: settings?.crmOptions?.logOnCustomer !== false, createLead: settings?.crmOptions?.createLead !== false },
    collectInfo: settings?.collectInfo || {
      name: true,
      email: true,
      phone: true,
      message: false
    },
    notifications: settings?.notifications || {
      email: true,
      sms: false,
      reminderHours: [24, 2]
    },
    meetingOptions: settings?.meetingOptions || {
      enableOnlineMeeting: true,
      defaultMeetingType: 'hybrid',
      allowClientChoice: true,
      autoGenerateMeetLink: false,
      meetingInstructions: '',
      selectedCalendarId: 'primary',
      selectedCalendarName: 'Elsődleges naptár'
    },
    isActive: settings?.isActive ?? true,
    organizationId: user?.organizationId || '',
    createdBy: user?.uid || ''
  });

  const [isSaving, setIsSaving] = useState(false);

  const updateFormData = useCallback((field: keyof BookingSettings, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateNestedField = useCallback((parent: keyof BookingSettings, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent] as any),
        [field]: value
      }
    }));
  }, []);

  const handleSave = async () => {
    if (!formData.title || !user?.organizationId) return;

    setIsSaving(true);
    try {
      if (settings?.id) {
        // Frissítés
        await appointmentService.updateBookingSettings(settings.id, formData);
        onSave({ ...settings, ...formData } as BookingSettings);
      } else {
        // Új létrehozás
        const id = await appointmentService.createBookingSettings({
          ...formData,
          organizationId: user.organizationId,
          createdBy: user.uid
        } as Omit<BookingSettings, 'id' | 'createdAt' | 'updatedAt'>);
        onSave({ ...formData, id } as BookingSettings);
      }
    } catch (error) {
      console.error('Error saving booking settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', name: 'Alapadatok', icon: Cog6ToothIcon },
    { id: 'schedule', name: 'Ütemezés', icon: CalendarDaysIcon },
    { id: 'collection', name: 'Adatgyűjtés', icon: UserIcon },
    { id: 'meeting', name: 'Meeting Típus', icon: VideoCameraIcon },
    { id: 'notifications', name: 'Értesítések', icon: BellIcon },
  ] as const;

  const weekDays = [
    { id: 1, name: 'Hétfő', short: 'H' },
    { id: 2, name: 'Kedd', short: 'K' },
    { id: 3, name: 'Szerda', short: 'Sze' },
    { id: 4, name: 'Csütörtök', short: 'Cs' },
    { id: 5, name: 'Péntek', short: 'P' },
    { id: 6, name: 'Szombat', short: 'Szo' },
    { id: 0, name: 'Vasárnap', short: 'V' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Tab navigáció */}
      <div className="border-b border-[var(--crm-border)] bg-[var(--crm-surface)]">
        <nav className="flex gap-1 px-3 sm:px-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tartalom */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[var(--crm-bg)] sm:min-h-[52vh]">
        <div className="max-w-4xl mx-auto">
          {/* Alapadatok tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="crm-card p-4 sm:p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg">
                    <Cog6ToothIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                    Szolgáltatás Információk
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Szolgáltatás neve *
                    </label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => updateFormData('title', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="pl. Konzultáció, Bemutató, Tervezési megbeszélés"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Leírás
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                      placeholder="Rövid leírás a szolgáltatásról, amit az ügyfelek látni fognak..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Időtartam (perc) *
                      </label>
                      <div className="relative">
                        <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="number"
                          value={formData.duration || ''}
                          onChange={(e) => updateFormData('duration', parseInt(e.target.value) || 60)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          min="15"
                          max="480"
                          placeholder="60"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Szünet időpontok között (perc)
                      </label>
                      <input
                        type="number"
                        value={formData.bufferTime || ''}
                        onChange={(e) => updateFormData('bufferTime', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        min="0"
                        max="60"
                        placeholder="15"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <ShieldCheckIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Manuális jóváhagyás szükséges
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          A foglalások csak jóváhagyás után lesznek véglegesek
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateFormData('requireConfirmation', !formData.requireConfirmation)}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.requireConfirmation ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${formData.requireConfirmation ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'basic' && (
            <div className="crm-card p-4 sm:p-5 mt-6">
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">CRM összekötés</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4">Mi történjen a foglalóval a CRM-ben? Az e-mail-cím alapján ismerjük fel.</p>
              <div className="space-y-3">
                {([
                  { key: 'logOnCustomer', title: 'Meglévő ügyfélnél kapcsolati naplóbejegyzés', desc: 'Ha a foglaló e-mailje egy ügyfélhez tartozik, a foglalás (és a lemondás/átütemezés) bekerül az ügyfélkapcsolati naplójába.' },
                  { key: 'createLead', title: 'Ismeretlen foglalóból új lead', desc: 'Ha nincs ilyen ügyfél, új lead jön létre „Időpontfoglaló” forrással, a foglalás napjára beállított követéssel. Meglévő leadnél megjegyzés kerül a leadhez.' },
                ] as const).map(o => {
                  const on = (formData.crmOptions as any)?.[o.key] !== false;
                  return (
                    <div key={o.key} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-[var(--crm-border)]">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{o.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{o.desc}</p>
                      </div>
                      <Switch checked={on} onChange={(v) => updateFormData('crmOptions', { ...(formData.crmOptions || {}), [o.key]: v })} size="sm" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ütemezés tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-6">
              {/* Munkaidő */}
              <div className="crm-card p-4 sm:p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                    Munkaidő
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nap kezdete
                    </label>
                    <input
                      type="time"
                      value={formData.availableHours?.start || '09:00'}
                      onChange={(e) => updateNestedField('availableHours', 'start', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nap vége
                    </label>
                    <input
                      type="time"
                      value={formData.availableHours?.end || '17:00'}
                      onChange={(e) => updateNestedField('availableHours', 'end', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Elérhető napok
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((day) => (
                      <button
                        key={day.id}
                        onClick={() => {
                          const currentDays = formData.availableHours?.days || [];
                          const newDays = currentDays.includes(day.id)
                            ? currentDays.filter(d => d !== day.id)
                            : [...currentDays, day.id];
                          updateNestedField('availableHours', 'days', newDays);
                        }}
                        className={`
                          p-3 rounded-lg border text-sm font-medium transition-all
                          ${formData.availableHours?.days?.includes(day.id)
                            ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                          }
                        `}
                      >
                        <div className="text-center">
                          <div className="font-semibold">{day.short}</div>
                          <div className="text-xs opacity-75">{day.name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Előrejelzési beállítások */}
              <div className="crm-card p-4 sm:p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                    <CalendarDaysIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                    Foglalási Korlátok
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Minimum előrejelzés (óra)
                    </label>
                    <input
                      type="number"
                      value={formData.advanceBooking?.min || ''}
                      onChange={(e) => updateNestedField('advanceBooking', 'min', parseInt(e.target.value) || 2)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      min="0"
                      max="168"
                      placeholder="2"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Hány órával előre lehet legkorábban foglalni
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum előrejelzés (nap)
                    </label>
                    <input
                      type="number"
                      value={formData.advanceBooking?.max || ''}
                      onChange={(e) => updateNestedField('advanceBooking', 'max', parseInt(e.target.value) || 30)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      min="1"
                      max="365"
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Hány nappal előre lehet legkésőbb foglalni
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Adatgyűjtés tab */}
          {activeTab === 'collection' && (
            <div className="space-y-6">
              <div className="crm-card p-4 sm:p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                    <UserIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                    Ügyfél Adatok Gyűjtése
                  </h3>
                </div>

                <div className="space-y-4">
                  {[
                    { key: 'name', label: 'Név', icon: UserIcon, required: true },
                    { key: 'email', label: 'Email cím', icon: EnvelopeIcon, required: true },
                    { key: 'phone', label: 'Telefonszám', icon: PhoneIcon, required: false },
                    { key: 'message', label: 'Üzenet/Megjegyzés', icon: ChatBubbleLeftRightIcon, required: false },
                  ].map((field) => (
                    <div key={field.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center space-x-3">
                        <field.icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {field.required ? 'Kötelező mező' : 'Opcionális mező'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => updateNestedField('collectInfo', field.key, !formData.collectInfo?.[field.key as keyof typeof formData.collectInfo])}
                        disabled={field.required}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                          ${field.required ? 'bg-gray-300 cursor-not-allowed' : ''}
                          ${formData.collectInfo?.[field.key as keyof typeof formData.collectInfo] ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${formData.collectInfo?.[field.key as keyof typeof formData.collectInfo] ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Meeting Típus tab */}
          {activeTab === 'meeting' && (
            <div className="space-y-6">
              <div className="crm-card p-4 sm:p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                    <VideoCameraIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                    Meeting Beállítások
                  </h3>
                </div>

                <div className="space-y-6">
                  {/* Online meeting engedélyezése */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Online meeting engedélyezése
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Lehetővé teszi az online találkozókat
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateNestedField('meetingOptions', 'enableOnlineMeeting', !formData.meetingOptions?.enableOnlineMeeting)}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.meetingOptions?.enableOnlineMeeting ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}
                      `}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                          transition duration-200 ease-in-out
                          ${formData.meetingOptions?.enableOnlineMeeting ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Alapértelmezett meeting típus */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Alapértelmezett meeting típus
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Személyes */}
                      <button
                        type="button"
                        onClick={() => updateNestedField('meetingOptions', 'defaultMeetingType', 'in_person')}
                        className={`
                          relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                          ${formData.meetingOptions?.defaultMeetingType === 'in_person'
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                          }
                        `}
                      >
                        <MapPinIcon className={`
                          h-5 w-5 
                          ${formData.meetingOptions?.defaultMeetingType === 'in_person'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-400'
                          }
                        `} />
                        <div className="text-left">
                          <div className={`
                            text-sm font-medium
                            ${formData.meetingOptions?.defaultMeetingType === 'in_person'
                              ? 'text-green-900 dark:text-green-100'
                              : 'text-gray-900 dark:text-gray-100'
                            }
                          `}>
                            Személyes
                          </div>
                        </div>
                        {formData.meetingOptions?.defaultMeetingType === 'in_person' && (
                          <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400 absolute top-2 right-2" />
                        )}
                      </button>

                      {/* Online */}
                      <button
                        type="button"
                        onClick={() => updateNestedField('meetingOptions', 'defaultMeetingType', 'online')}
                        className={`
                          relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                          ${formData.meetingOptions?.defaultMeetingType === 'online'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                          }
                        `}
                      >
                        <VideoCameraIcon className={`
                          h-5 w-5 
                          ${formData.meetingOptions?.defaultMeetingType === 'online'
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-400'
                          }
                        `} />
                        <div className="text-left">
                          <div className={`
                            text-sm font-medium
                            ${formData.meetingOptions?.defaultMeetingType === 'online'
                              ? 'text-primary-900 dark:text-primary-100'
                              : 'text-gray-900 dark:text-gray-100'
                            }
                          `}>
                            Online
                          </div>
                        </div>
                        {formData.meetingOptions?.defaultMeetingType === 'online' && (
                          <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 absolute top-2 right-2" />
                        )}
                      </button>

                      {/* Hibrid */}
                      <button
                        type="button"
                        onClick={() => updateNestedField('meetingOptions', 'defaultMeetingType', 'hybrid')}
                        className={`
                          relative flex items-center space-x-3 p-4 border rounded-lg transition-all duration-200
                          ${formData.meetingOptions?.defaultMeetingType === 'hybrid'
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                          }
                        `}
                      >
                        <GlobeAltIcon className={`
                          h-5 w-5 
                          ${formData.meetingOptions?.defaultMeetingType === 'hybrid'
                            ? 'text-purple-600 dark:text-purple-400'
                            : 'text-gray-400'
                          }
                        `} />
                        <div className="text-left">
                          <div className={`
                            text-sm font-medium
                            ${formData.meetingOptions?.defaultMeetingType === 'hybrid'
                              ? 'text-purple-900 dark:text-purple-100'
                              : 'text-gray-900 dark:text-gray-100'
                            }
                          `}>
                            Hibrid
                          </div>
                        </div>
                        {formData.meetingOptions?.defaultMeetingType === 'hybrid' && (
                          <CheckIcon className="h-4 w-4 text-purple-600 dark:text-purple-400 absolute top-2 right-2" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Kliens választhat */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Kliens választhat meeting típust
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        A kliens dönthet személyes vagy online találkozó között
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateNestedField('meetingOptions', 'allowClientChoice', !formData.meetingOptions?.allowClientChoice)}
                      className={`
                        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.meetingOptions?.allowClientChoice ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}
                      `}
                    >
                      <span
                        className={`
                          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                          transition duration-200 ease-in-out
                          ${formData.meetingOptions?.allowClientChoice ? 'translate-x-5' : 'translate-x-0'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Online link forrása */}
                  {formData.meetingOptions?.enableOnlineMeeting && (
                    <div className="border-t border-[var(--crm-border)] pt-5">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50 mb-1">Honnan jöjjön az online meeting link?</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">A linket a foglalás megerősítő e-mailje automatikusan tartalmazza.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {([
                          { id: 'google', title: 'Google Naptár + Meet', desc: 'Esemény a saját naptáradban, egyedi Meet link minden foglaláshoz. Egyszeri összekapcsolás.' },
                          { id: 'manual', title: 'Saját fix link', desc: 'Egy állandó link (Meet személyes szoba, Zoom, Teams). Nincs integráció.' },
                        ] as const).map(opt => {
                          const active = (formData.meetingOptions?.linkProvider || (formData.meetingOptions?.autoGenerateMeetLink ? 'google' : 'manual')) === opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                updateNestedField('meetingOptions', 'linkProvider', opt.id);
                                updateNestedField('meetingOptions', 'autoGenerateMeetLink', opt.id === 'google');
                              }}
                              className={`text-left p-3 rounded-lg border transition-colors ${active ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10' : 'border-[var(--crm-border)] hover:border-gray-300 dark:hover:border-gray-600'}`}
                            >
                              <div className="text-[13px] font-medium text-gray-900 dark:text-gray-50">{opt.title}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">{opt.desc}</div>
                            </button>
                          );
                        })}
                      </div>

                      {(formData.meetingOptions?.linkProvider || (formData.meetingOptions?.autoGenerateMeetLink ? 'google' : 'manual')) === 'google' && (
                        <div className="mt-3">
                          <GoogleAuthButton />
                        </div>
                      )}

                      {(formData.meetingOptions?.linkProvider || (formData.meetingOptions?.autoGenerateMeetLink ? 'google' : 'manual')) === 'manual' && (
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Meeting link</label>
                          <input
                            type="url"
                            value={formData.meetingOptions?.meetingLink || ''}
                            onChange={(e) => updateNestedField('meetingOptions', 'meetingLink', e.target.value)}
                            placeholder="https://meet.google.com/xxx-xxxx-xxx vagy Zoom / Teams link"
                            className="w-full h-9 border rounded-lg px-3 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Meeting instrukciók */}
                  {formData.meetingOptions?.enableOnlineMeeting && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Meeting instrukciók
                      </label>
                      <textarea
                        value={formData.meetingOptions?.meetingInstructions || ''}
                        onChange={(e) => updateNestedField('meetingOptions', 'meetingInstructions', e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        placeholder="Pl.: A meeting link automatikusan elküldésre kerül az email megerősítésben..."
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Ezek az instrukciók megjelennek a kliens számára a foglalás megerősítésében
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Értesítések tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="crm-card p-4 sm:p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                    <BellIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">
                    Értesítési Beállítások
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <EnvelopeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Email értesítések
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Visszaigazolás és emlékeztetők emailben
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNestedField('notifications', 'email', !formData.notifications?.email)}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                        ${formData.notifications?.email ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${formData.notifications?.email ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          SMS értesítések
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Emlékeztetők SMS-ben (hamarosan)
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => updateNestedField('notifications', 'sms', !formData.notifications?.sms)}
                      disabled={true} // Egyelőre letiltva
                      className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 cursor-not-allowed"
                    >
                      <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                    </button>
                  </div>

                  {formData.notifications?.email && (
                    <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                      <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Emlékeztető időpontok (órában az időpont előtt)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 4, 8, 24, 48, 72].map((hours) => (
                          <button
                            key={hours}
                            onClick={() => {
                              const currentReminders = formData.notifications?.reminderHours || [];
                              const newReminders = currentReminders.includes(hours)
                                ? currentReminders.filter(h => h !== hours)
                                : [...currentReminders, hours].sort((a, b) => b - a);
                              updateNestedField('notifications', 'reminderHours', newReminders);
                            }}
                            className={`
                              px-3 py-2 rounded-lg text-sm font-medium transition-all
                              ${formData.notifications?.reminderHours?.includes(hours)
                                ? 'bg-primary-600 text-white shadow-md'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                              }
                            `}
                          >
                            {hours}h
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alsó akció sáv */}
      <div className="border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className={`
                w-3 h-3 rounded-full
                ${formData.isActive ? 'bg-green-500' : 'bg-gray-400'}
              `} />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {formData.isActive ? 'Aktív' : 'Inaktív'}
              </span>
            </div>
            <button
              onClick={() => updateFormData('isActive', !formData.isActive)}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            >
              {formData.isActive ? 'Deaktiválás' : 'Aktiválás'}
            </button>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
              className="flex items-center space-x-2"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Mégse</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !formData.title}
              className="flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Mentés...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  <span>{settings ? 'Frissítés' : 'Létrehozás'}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSettingsForm;
